import * as queryString from './QueryString';
import {
  getCurrentRouteName,
  getRouteParams,
  getRouteQuery,
  isCustomerOrdersRoute,
  isReviewRoute,
  NAME as ROUTE_NAME
} from './Route';
import requestLib from './Request';
import EventBus from './EventBus';
import { deleteProductCache } from './ProductCache';
import {
  redirectToErrorPage,
  redirectToGalleryView,
  redirectToHomeGrid,
  getTwoDigitDecimal,
  isSearchResultView,
  getStoredSearchTerm,
  getReturnUrl,
  logException,
  FONT_TYPES,
  DEFAULT_FONT_FAMILY,
  CUSTOM_COMPONENT_TYPES
} from './Utils';
import { getFormattedPrice } from './locale';
import { getProductSetQuantityMultiplier } from './Product';
import { getProductInventory, getShowOutOfProductConfig } from './Inventory';
import { VIEW_TYPES } from '../molecules/OutOfStockBadge/index';
import { getObjectValues } from '../../../utils/GlobalUtils';
import { getCustomPageAmalgamObject, getCustomPageProducts } from './CustomPageAmalgam';
import { getCachedScrollPositionForPage, setCachedScrollPositionForPage } from './ScrollPosition';

const PANTALOONS_IGNORE_GENDER_FOR_SIZE_GUIDE = ['Home Decor', 'Unisex'];
const COMMON_AMALGAM_KEY = 'common';
const SEARCH_RESULTS_AMALGAM_KEY = 'searchResults';

const defaultShippingConfiguration = {
  country: { required: true },
  state: { required: true },
  city: { required: true },
  pincode: { required: false }
};

const getAmalgamPromises = {};
const amalgamObjects = {};
const metaDataObjects = {};
const companyLogoDimensions = {};
let singlePageVirtuosoCache = {};

let showBanner = true;
let customFontsInitialized = false;

const clearSinglePageVirtuosoCache = () => {
  singlePageVirtuosoCache = {};
};

EventBus.addListener(EventBus.TYPE.CLEAR_SINGLE_PAGE_VIRTUOSO_CACHE, clearSinglePageVirtuosoCache);

export const getSinglePageVirtuosoCache = () => ({ ...singlePageVirtuosoCache });

export const setSinglePageVirtuosoCache = (cache = {}) => {
  singlePageVirtuosoCache = { ...cache };
};

export const setCommonAmalgam = (amalgam) => {
  if (!amalgam.visitorId) {
    amalgam.visitorId = (amalgamObjects[COMMON_AMALGAM_KEY] || {}).visitorId;
  }
  amalgamObjects[COMMON_AMALGAM_KEY] = amalgam;
};

const setCommonAmalgamFromSingle = (amalgam) => {
  if (!amalgam) {
    return;
  }
  setCommonAmalgam({
    companyId: amalgam.companyId,
    companyName: amalgam.companyName,
    companyAddress: amalgam.companyAddress,
    companyDetails: amalgam.companyDetails,
    companyDomain: amalgam.companyDomain,
    companyDomains: amalgam.companyDomains,
    currencyCode: amalgam.currencyCode,
    currencySymbol: amalgam.currencySymbol,
    logoExists: amalgam.logoExists,
    logoVersion: amalgam.logoVersion,
    currencyOptions: amalgam.currencyOptions,
    isBlockedCompany: amalgam.isBlockedCompany,
    localeNumberFormats: amalgam.localeNumberFormats,
    paymentPlan: amalgam.paymentPlan,
    extendedTrialStart: amalgam.extendedTrialStart,
    i18n: amalgam.i18n,
    companyExperiments: amalgam.companyExperiments,
    visitorId: amalgam.visitorId,
    pureWhitelabel: amalgam.pureWhitelabel,
    showCreateFreeCatalogueBanner: amalgam.showCreateFreeCatalogueBanner,
    domain: amalgam.domain,
    userId: amalgam.user ? amalgam.user.id : amalgam.userId,
    userPhone: amalgam.user
      ? amalgam.user.phone
        ? amalgam.user.phone
        : amalgam.user.userPhone
      : amalgam.userPhone,
    userName: amalgam.user
      ? amalgam.user.name
        ? amalgam.user.name
        : amalgam.user.userName
      : amalgam.userName,
    userEmail: amalgam.user
      ? amalgam.user.email
        ? amalgam.user.email
        : amalgam.user.userEmail
      : amalgam.userEmail,
    isPaid: amalgam.isPaid,
    planType: amalgam.planType,
    theme: amalgam.theme || amalgam.catalogueTheme,
    facebookPixelIds: amalgam.facebookPixelIds,
    googleAnalyticsIds: amalgam.companyExperiments.googleAnalytics,
    productType: amalgam.companyExperiments.productType,
    shippingConfiguration: amalgam.shippingConfiguration,
    showReportAbuseLink: amalgam.showReportAbuseLink,
    isHomePage: amalgam.isHomePage,
    isCustomPage: amalgam.isCustomPage,
    hasCustomFrontPage: amalgam.hasCustomFrontPage,
    menuItems: amalgam.menuItems,
    isCustomerOrderAmalgam: amalgam.isCustomerOrderAmalgam,
    orders: amalgam.orders,
    orderDetails: amalgam.orderDetails,
    customFooter: amalgam.customFooter,
    customFooterTheme: amalgam.customFooterTheme,
    customComponents: amalgam.customComponents,
    customFonts: amalgam.customFonts,
    unitDetails: amalgam.unitDetails
  });
};

if (window.metaDataObject && window.metaDataObject.logoHeight && window.metaDataObject.logoWidth) {
  companyLogoDimensions.logoHeight = (window.metaDataObject || {}).logoHeight || 30;
  companyLogoDimensions.logoWidth = (window.metaDataObject || {}).logoWidth || 100;
  companyLogoDimensions.logoAspectRatio =
    companyLogoDimensions.logoWidth / companyLogoDimensions.logoHeight;
}

if (window.amalgamObject && window.amalgamObject.isMobile) {
  const key = COMMON_AMALGAM_KEY;
  amalgamObjects[key] = window.amalgamObject;
  metaDataObjects[key] = window.metaDataObject;
  setCommonAmalgamFromSingle(amalgamObjects[key]);
}

if (window.amalgamObject && window.amalgamObject.isSingle) {
  const { visitorId, companySlug, catalogueSlug, randomSlug, companyDetailSlug } =
    window.amalgamObject.routeSlugs;
  let key = companyDetailSlug
    ? `${window.amalgamObject.companyId}_${companyDetailSlug}`
    : `${companySlug}_${catalogueSlug}_${randomSlug}`;

  if (window.amalgamObject.isCustomerOrderAmalgam) {
    key = `${companySlug}_${window.amalgamObject.companyId}_${visitorId}`;
  }
  amalgamObjects[key] = window.amalgamObject;
  metaDataObjects[key] = window.metaDataObject;
  setCommonAmalgamFromSingle(amalgamObjects[key]);
}

if (window.amalgamObject && window.amalgamObject.isGlobalReview) {
  const key = COMMON_AMALGAM_KEY;
  amalgamObjects[key] = { ...window.amalgamObject };
  amalgamObjects[key].theme = amalgamObjects[key].catalogueTheme;
  delete amalgamObjects[key].catalogueTheme;
  metaDataObjects[key] = window.metaDataObject;
}

export const getAmalgam = ({
  companySlug,
  catalogueSlug,
  randomSlug,
  companyDetailSlug,
  productId,
  fromBundle
}) => {
  const { domainTag, ref, referrer, referrerVal, themeId, hashedBroadcastReceiver } =
    getRouteQuery();

  const key = companyDetailSlug
    ? `${getCompanyId()}_${companyDetailSlug}`
    : `${companySlug}_${catalogueSlug}_${randomSlug}`;

  if (amalgamObjects[key]) {
    return Promise.resolve(amalgamObjects[key]);
  }

  if (getAmalgamPromises[key]) {
    return getAmalgamPromises[key];
  }

  getAmalgamPromises[key] = new Promise((resolve, reject) => {
    const query = queryString.stringify({
      companySlug,
      catalogueSlug,
      randomSlug,
      companyDetailSlug,
      companyId: getCompanyId(),
      productId,
      ref,
      referrer,
      referrerVal,
      domainTag,
      themeId,
      hashedBroadcastReceiver,
      fromModule: fromBundle
    });

    requestLib({
      method: 'GET',
      url: companyDetailSlug ? `/amalgam/company-detail?${query}` : `/amalgam?${query}`
    })
      .then((response) => {
        amalgamObjects[key] = response.amalgam;
        metaDataObjects[key] = response.metaData;
        delete getAmalgamPromises[key];
        setCommonAmalgamFromSingle(amalgamObjects[key]);
        resolve(response);
      })
      .catch((err) => {
        delete getAmalgamPromises[key];
        reject(err);
        if (err && err.message === 'SHOWCASE_EXPIRED') {
          window.location.href = '/s/error?errorType=expired';
        }
      });
  });

  return getAmalgamPromises[key];
};

export const getGlobalReviewAmalgam = ({ orderId }) => {
  const key = COMMON_AMALGAM_KEY;

  if (amalgamObjects[key]) {
    return Promise.resolve(amalgamObjects[key]);
  }

  if (getAmalgamPromises[key]) {
    return getAmalgamPromises[key];
  }

  getAmalgamPromises[key] = new Promise((resolve, reject) => {
    requestLib({
      method: 'GET',
      url: `/amalgam/order-cart?orderId=${orderId}&companyId=${getCompanyId()}&visitorId=${getVisitorId()}`
    })
      .then((response) => {
        amalgamObjects[key] = response.amalgam;
        metaDataObjects[key] = response.metaData;
        delete getAmalgamPromises[key];
        resolve(response);
      })
      .catch((err) => {
        delete getAmalgamPromises[key];
        reject(err);
      });
  });

  return getAmalgamPromises[key];
};

export const getCustomPageAmalgamPaginated = ({
  companyId,
  customPageId,
  customPageSlug,
  afterPosition
}) =>
  new Promise((resolve, reject) => {
    requestLib({
      method: 'GET',
      url: `/api/amalgam/custom-page/paginated?companyId=${companyId}&customPageId=${customPageId}&customPageSlug=${customPageSlug}&limit=5&afterPosition=${afterPosition}`
    })
      .then((response) => resolve(response))
      .catch((err) => {
        reject(err);
      });
  });

export const getUpdatedAmalgamPromises = (productId) =>
  new Promise((resolve, reject) => {
    requestLib({
      method: 'GET',
      url: `/api/update-search-amalgam?productId=${productId}`
    })
      .then((response) => {
        updateAmalgamForSearchClickedProduct(response);
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });

export const getCatalogueSlugs = ({ catalogueId }) =>
  new Promise((resolve, reject) => {
    requestLib({
      method: 'GET',
      url: `/api/catalogue/slugs?catalogueId=${catalogueId}`
    })
      .then((response) => resolve(response))
      .catch((err) => {
        reject(err);
      });
  });

export const isAmalgamFetched = ({
  companySlug,
  catalogueSlug,
  companyDetailSlug,
  randomSlug
} = {}) => {
  if (isCustomerOrdersRoute(getCurrentRouteName()) || isReviewRoute(getCurrentRouteName())) {
    return !!amalgamObjects[COMMON_AMALGAM_KEY];
  }

  if (companyDetailSlug) {
    return isCompanyDetailsAmalgamFetched({ companyDetailSlug });
  }

  const key = `${companySlug}_${catalogueSlug}_${randomSlug}`;

  return !!amalgamObjects[key];
};

export const isCompanyDetailsAmalgamFetched = ({ companyDetailSlug }) => {
  const key = `${getCompanyId()}_${companyDetailSlug}`;
  return !!amalgamObjects[key];
};

export const isSearchAmalgamFetched = () => !!amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY];

const getAmalgamKey = () => {
  const { companySlug, catalogueSlug, randomSlug, companyDetailSlug } = getRouteParams();
  const currentRouteName = getCurrentRouteName();

  if (isCustomerOrdersRoute(currentRouteName) || isReviewRoute(currentRouteName)) {
    return COMMON_AMALGAM_KEY;
  }

  if (
    (currentRouteName === ROUTE_NAME.COMPANY_DETAIL ||
      currentRouteName === ROUTE_NAME.GROUP ||
      currentRouteName === ROUTE_NAME.CUSTOM_PAGE ||
      currentRouteName === ROUTE_NAME.RESELLER_VIEW) &&
    isSearchResultView()
  ) {
    return SEARCH_RESULTS_AMALGAM_KEY;
  }

  if (
    (currentRouteName === ROUTE_NAME.GALLERY ||
      currentRouteName === ROUTE_NAME.IMAGE_VIEW ||
      currentRouteName === ROUTE_NAME.RESELLER_CATALOGUE_GALLERY ||
      currentRouteName === ROUTE_NAME.RESELLER_IMAGE_VIEW) &&
    getStoredSearchTerm() &&
    getReturnUrl()
  ) {
    return SEARCH_RESULTS_AMALGAM_KEY;
  }

  if (currentRouteName === ROUTE_NAME.COMPANY_DETAIL) {
    return `${getCompanyId()}_${companyDetailSlug}`;
  }

  return `${companySlug}_${catalogueSlug}_${randomSlug}`;
};

const getAmalgamObject = () => {
  const amalgamKey = getAmalgamKey();
  return amalgamObjects[amalgamKey] || {};
};

export const clearSingleAmalgams = () => {
  Object.keys(amalgamObjects).forEach((key) => {
    if (key === COMMON_AMALGAM_KEY) {
      return;
    }

    delete amalgamObjects[key];
  });

  EventBus.notify(EventBus.TYPE.CLEAR_AMALGAM);
};

export const isCustomPage = () => {
  const { customPageSlug } = getRouteParams();
  return (
    (amalgamObjects[COMMON_AMALGAM_KEY] || {}).isCustomPage ||
    (location.pathname === '/' && (amalgamObjects[COMMON_AMALGAM_KEY] || {}).hasCustomFrontPage) ||
    !!customPageSlug ||
    customPageSlug === ''
  );
};

export const hasCustomFrontPage = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {}).hasCustomFrontPage || isHomePage();

export const setVisitorId = (visitorId) => {
  if (amalgamObjects[COMMON_AMALGAM_KEY]) {
    amalgamObjects[COMMON_AMALGAM_KEY].visitorId = visitorId;
  }
};

export const isHomePage = () => {
  if (isSearchResultView()) {
    return false;
  }
  return (amalgamObjects[COMMON_AMALGAM_KEY] || {}).isHomePage;
};

export const getCurrencySymbol = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).currencySymbol;
export const getCurrencyCode = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).currencyCode;
export const isGOGCurrencyCode = () => getCurrencyCode() === 'GOG';
export const isIndianRupeeCurrencySelected = () => getCurrencyCode() === 'INR';
export const getCurrencyOptions = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).currencyOptions;

export const isCustomerOrderAmalgamAvailable = () => getAmalgamObject().isCustomerOrderAmalgam;
export const getCustomerOrdersFromAmalgam = () => getAmalgamObject().orders;
export const getCustomerOrderDetailsFromAmalgam = (orderId) =>
  (getAmalgamObject().orderDetails || {})[orderId];
export const getReviewOrder = (orderId) => ((getAmalgamObject() || {}).reviewOrders || {})[orderId];

export const getCatalogue = () => getAmalgamObject().catalogue || {};
export const checkHasFilters = () => !!(getAmalgamObject() || {}).hasFilters;
export const getCatalogueProductList = () => (getAmalgamObject().catalogue || {}).productList || {};
export const getCatalogueRouteSlugs = () => getAmalgamObject().routeSlugs || {};
export const getI18n = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).i18n || {};
export const getActionButtonTheme = () => getAmalgamObject().actionButtonTheme || {};
export const getInventory = () => getAmalgamObject().inventory || {};
export const setInventory = (inventory) => {
  if (amalgamObjects[getAmalgamKey()]) {
    amalgamObjects[getAmalgamKey()].inventory = inventory;
  }
};
export const getExperiments = () => {
  if (getCurrentRouteName() === ROUTE_NAME.CUSTOM_PAGE) {
    return getCustomPageAmalgamObject().experiments || {};
  }
  return getAmalgamObject().experiments || {};
};

export const getCompanyExperiments = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyExperiments || {};

export const getProduct = (productId, fromCustomPage) => {
  let product =
    fromCustomPage && !isSearchResultView()
      ? getCustomPageProducts().find((product) => product.id === productId)
      : getAmalgamObject()[productId];
  if (!product) {
    product = getAmalgamObject()[productId];
  }
  if (!product) {
    return null;
  }
  return product;
};

export const getThemeId = () => getAmalgamObject().catalogueThemeId;
export const getActionButtonThemeId = () => getAmalgamObject().actionButtonId || 'default';
export const getShowcase = () => getAmalgamObject().showcase || {};
export const getDomainTag = () => getAmalgamObject().domainTag;
export const getBase64DomainTag = () => getAmalgamObject().base64DomainTag;
export const getVisitorId = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).visitorId;
export const clearVisitorIdFromAmalgam = () => {
  if (amalgamObjects[COMMON_AMALGAM_KEY]) {
    amalgamObjects[COMMON_AMALGAM_KEY].visitorId = null;
  }
};
export const getCompanyId = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyId;
export const getUserIdFromAmalgam = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).userId;
export const getUserPhone = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).userPhone;
export const getUserName = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).userName;
export const getUserEmail = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).userEmail;
export const getCompanyFacebookPixelIds = () =>
  ((amalgamObjects[COMMON_AMALGAM_KEY] || {}).facebookPixelIds || [])
    .filter((facebookTrackingId) => !!facebookTrackingId)
    .filter((v, i, a) => a.indexOf(v) === i);
export const getUniversalGoogleAnalyticsId = () => {
  const googleAnalytics = ((amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyExperiments || {})
    .googleAnalytics;
  if (!!googleAnalytics && googleAnalytics.startsWith('UA-')) {
    return googleAnalytics;
  }
  return null;
};
export const getCompanyGoogleAnalyticsIds = () =>
  [
    ((amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyExperiments || {}).googleAnalytics,
    ((amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyExperiments || {}).googleTagManagerId,
    ((amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyExperiments || {})
      .googleAnalyticsMeasurementId
  ]
    .filter((googleTrackingId) => !!googleTrackingId)
    .filter((v, i, a) => a.indexOf(v) === i);
export const getCompanyProductType = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyExperiments.productType || [];
export const getCompanyName = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyName;
export const getHeaderMenuItems = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).menuItems || [];
export const getPaymentResponse = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).paymentResponse;
export const getCompanyAddress = () => getAmalgamObject().companyAddress || {};
export const getUser = () => getAmalgamObject().user || {};
export const getLogoVersion = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).logoVersion;
export const getAltLogoVersion = () => getAmalgamObject().altLogoVersion;
export const shouldLoadCloudfront = () => getAmalgamObject().loadCloudfront;
export const getRequestGeo = () => getAmalgamObject().requestGeo;
export const getVisitorFromAmalgam = () => {
  const amalgam = getAmalgamObject();
  return {
    id: amalgam.visitorId,
    phone: getVisitorPhone(),
    phoneVerified: getVisitorPhoneVerified()
  };
};
export const getVisitorPhone = () => getAmalgamObject().visitorPhone;
export const getVisitorPhoneVerified = () => getAmalgamObject().visitorPhoneVerified;
export const isPaid = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).isPaid;
export const getPlanType = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).planType;
export const getReferrer = () => getAmalgamObject().referrer;
export const getReferrerVal = () => getAmalgamObject().referrerVal;
export const doesLogoExists = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).logoExists;
export const getLogoHeight = () => companyLogoDimensions.logoHeight || 30;
export const getLogoWidth = () => companyLogoDimensions.logoWidth || 100;
export const getLogoAspectRatio = () => getLogoWidth() / getLogoHeight();
export const getSiteTitle = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {}).companyDetails.sitetitle;
export const doesAltLogoExists = () => getAmalgamObject().altLogoExists;
export const getHashedBroadcastReceiver = () => getAmalgamObject().hashedBroadcastReceiver;
export const hasShippingConfiguration = () =>
  !!Object.keys(getAmalgamObject().shippingConfiguration || {}).length;
export const getShippingConfiguration = () =>
  hasShippingConfiguration()
    ? getAmalgamObject().shippingConfiguration
    : defaultShippingConfiguration;
export const getPureWhitelabel = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).pureWhitelabel;
export const showCreateFreeCatalogueBannerHighVisiblity = () =>
  getAmalgamObject().showCreateFreeCatalogueBannerHighVisiblity;
export const showCreateFreeCatalogueBanner = () =>
  showBanner &&
  (getAmalgamObject().showCreateFreeCatalogueBannerHighVisiblity ||
    getAmalgamObject().showCreateFreeCatalogueBannerLowVisiblity);
export const onBannerClose = () => {
  showBanner = false;
};
export const showCatalogueBanner = () => getAmalgamObject().showCatalogueBanners;
export const getCatalogueBanner = () => getAmalgamObject().catalogueBanners;
export const isSimulation = () => getAmalgamObject().isSimulation;
export const getSimulatedVisitor = () => getAmalgamObject().simulatedVisitor;
export const getLocaleNumberFormats = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {}).localeNumberFormats;
export const getCompanyDomains = () => getAmalgamObject().companyDomains || {};
export const getCompanyDetails = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {} || {}).companyDetails || {};
export const getDomain = () => getAmalgamObject().domain;
export const disableAnalytics = () => getAmalgamObject().disableAnalytics;
export const getCoverProductId = () => getAmalgamObject().coverProductId;
export const getPaymentStatus = () => getAmalgamObject().paymentStatus || '';
export const showOnSaleTag = () => !!getAmalgamObject().showOnSaleTag;
export const showInStockTag = () => !!getAmalgamObject().showInStockTag;
export const getCatalogueTheme = () => (amalgamObjects[COMMON_AMALGAM_KEY] || {}).theme || {};
export const getAllColorOptions = (productId, fromCustomPage) => {
  const product = getProduct(productId, fromCustomPage);
  return product && product.allOptions && product.allOptions.COLORS
    ? product.allOptions.COLORS
    : null;
};
export const getAllSizeOptions = (productId, fromCustomPage) => {
  const product = getProduct(productId, fromCustomPage);
  return product && product.allOptions && product.allOptions.SIZES
    ? product.allOptions.SIZES
    : null;
};

export const catalogueHasVariants = (viewType) => {
  if (viewType === VIEW_TYPES.SEARCH_GRID) {
    return true;
  }
  return getAmalgamObject().catalogueHasVariants;
};

const productWithOptionsRedirection = (defaultVariantId) => {
  redirectToGalleryView(defaultVariantId);
};

const deletedVariantRedirection = (parentProductId) => {
  const amalgamObject = getAmalgamObject();
  const parentProduct = amalgamObject[parentProductId];

  if (!parentProduct || parentProduct.dateDeleted) {
    redirectToHomeGrid();
    return true;
  }

  const { defaultVariantId } = parentProduct;

  if (!defaultVariantId) {
    logException("Product doesn't have a default variant", { defaultVariantId, parentProductId });
    redirectToErrorPage();
    return;
  }

  productWithOptionsRedirection(defaultVariantId);
};

export const validateEndpoint = ({ productId, replace = false } = {}) => {
  if (!productId) {
    return;
  }

  const amalgamObject = getAmalgamObject();
  const product = amalgamObject[productId];

  if (!product) {
    redirectToHomeGrid({ replace });
    return true;
  }

  const { parentProductId, defaultVariantId, dateDeleted } = product;
  const { count } = amalgamObject.inventory[productId] || {};
  const showOutOfStockProduct = getShowOutOfProductConfig(productId);

  if (!parentProductId && !dateDeleted && defaultVariantId) {
    productWithOptionsRedirection(defaultVariantId);
    return;
  }

  if (parentProductId && (dateDeleted || (count === 0 && showOutOfStockProduct !== true))) {
    deletedVariantRedirection(parentProductId);
  }
};

export const mergeProductsIntoDefaultVariants = ({ productIds = [] } = {}) => {
  const amalgamObject = getAmalgamObject();
  const { productList } = amalgamObject.catalogue || {};

  if (!productList) {
    return;
  }

  let mergeProductList = Object.keys(productList || {}) || [];

  const extraProducts = productIds.filter((id) => !!id);
  if (Array.isArray(extraProducts) && extraProducts.length) {
    mergeProductList = [...mergeProductList, ...extraProducts];
  }

  mergeProductList.forEach((productId) => {
    if (!amalgamObject[productId]) {
      // TODO:
      // throw error
      return;
    }

    const product = amalgamObject[productId];
    if (!product || !product.id || !product.parentProductId) {
      return;
    }

    const parentProduct = amalgamObject[product.parentProductId];
    const mergedProduct = mergeProductAndVariant(parentProduct, product);
    const mergedInventory = mergeProductVariantInventory({
      parentProduct,
      inventory: getProductInventory(product.id)
    });

    if (mergedProduct && mergedProduct.id && extraProducts.indexOf(mergedProduct.id) > -1) {
      deleteProductCache(mergedProduct.id);
    }

    amalgamObject[product.id] = mergedProduct || product;
    if (Object.keys(mergedInventory || {}).length) {
      amalgamObject.inventory[product.id] = mergedInventory;
    }
  });
};

export const mergeProductsIntoNonDefaultVariants = () => {
  const amalgamObject = getAmalgamObject();
  const { productList } = amalgamObject.catalogue || {};

  const bulkOrderingViewEnabled = (getExperiments().bulkOrderingView || {}).id;
  if (!bulkOrderingViewEnabled) {
    // return;
  }

  const mergeProductList = [];
  Object.keys(productList || {}).forEach((productId) => {
    const product = getProduct(productId);
    if (!product || !product.parentProductId) {
      return;
    }

    const parentProduct = getProduct(product.parentProductId);

    ((parentProduct && parentProduct.availableVariants) || []).forEach((variantId) => {
      // if (variantId !== parentProduct.defaultVariantId) {
      mergeProductList.push(variantId);
      // }
    });
  });

  mergeProductList.forEach((productId) => {
    if (!amalgamObject[productId]) {
      // TODO:
      // throw error
      return;
    }

    const product = amalgamObject[productId];
    if (!product || !product.id || !product.parentProductId) {
      return;
    }

    const parentProduct = amalgamObject[product.parentProductId];
    const mergedProduct = mergeProductAndVariant(parentProduct, product);
    const mergedInventory = mergeProductVariantInventory({
      parentProduct,
      inventory: getProductInventory(product.id)
    });

    amalgamObject[product.id] = mergedProduct || product;
    if (Object.keys(mergedInventory || {}).length) {
      amalgamObject.inventory[product.id] = mergedInventory;
    }
  });
};

/**
  Price/ Discounted Price inherited from product may be divided by the set quantity
  if the product is a set. Hence multiple the price by the product multiplier to get
  the actual price for this variant and then divide by the variant set multiplier
  to get the final price for the variant
*/
const processVariantInheritedPrice = ({ price, variantSetMultiplier, productSetMultiplier }) => {
  price *= productSetMultiplier;
  price = getTwoDigitDecimal(price / variantSetMultiplier);
  return price;
};

export const mergeProductAndVariant = (product, variant, fromCustomPage) => {
  if (!product || !variant) {
    return null;
  }

  const mergedVariant = { ...variant };

  if (!mergedVariant.name) {
    mergedVariant.name = product.name;
  }

  const variantSetMultiplier = getProductSetQuantityMultiplier({
    product: variant,
    fromCustomPage
  });
  const productSetMultiplier = getProductSetQuantityMultiplier({ product, fromCustomPage });
  const variantHasPrice = typeof mergedVariant.price === 'number';
  if (!variantHasPrice) {
    mergedVariant.currency = product.currency;
    mergedVariant.price = product.price;
    mergedVariant.discounted_price = product.discounted_price;

    mergedVariant.formattedDiscountedPrice = product.formattedDiscountedPrice;
    if (typeof mergedVariant.discounted_price === 'number') {
      mergedVariant.discounted_price = processVariantInheritedPrice({
        price: mergedVariant.discounted_price,
        variantSetMultiplier,
        productSetMultiplier
      });
      mergedVariant.formattedDiscountedPrice = getFormattedPrice(mergedVariant.discounted_price);
    }

    if (typeof mergedVariant.price === 'number') {
      mergedVariant.price = processVariantInheritedPrice({
        price: mergedVariant.price,
        variantSetMultiplier,
        productSetMultiplier
      });
      mergedVariant.formattedPrice = getFormattedPrice(mergedVariant.price);
    }
  }

  if (!mergedVariant.description) {
    mergedVariant.description = product.description;
    mergedVariant.formattedDescription = product.formattedDescription;
  }

  if (!mergedVariant.pictures || !Object.keys(mergedVariant.pictures).length) {
    const productPictures = product.pictures || {};
    mergedVariant.pictures = {
      ...productPictures
    };
  }

  if (
    !mergedVariant.default_picture_id ||
    !mergedVariant.pictures ||
    !mergedVariant.pictures[mergedVariant.default_picture_id]
  ) {
    mergedVariant.default_picture_id = product.default_picture_id;
    mergedVariant.pictureUrl = product.pictureUrl;
    mergedVariant.prepared = product.prepared;
  }

  if (!mergedVariant.videos || Object.keys(mergedVariant.videos).length === 0) {
    mergedVariant.videos = product.videos;
  }

  if (product.taxes && product.taxes.length) {
    if (!mergedVariant.taxes || !mergedVariant.taxes.length) {
      mergedVariant.taxes = product.taxes.map((row) => ({ ...row }));
    }

    let finalPrice = null;

    if (typeof mergedVariant.discounted_price === 'number') {
      finalPrice = mergedVariant.discounted_price;
    } else if (typeof mergedVariant.price === 'number') {
      finalPrice = mergedVariant.price;
    }

    mergedVariant.taxedPrice = null;
    const taxPercentage = Number(mergedVariant.taxes[0].taxPercentage);
    if (typeof finalPrice === 'number') {
      mergedVariant.taxedPrice = finalPrice + finalPrice * (taxPercentage / 100);
    }
    mergedVariant.formattedTaxedPrice = getFormattedPrice(mergedVariant.taxedPrice);
  }

  if (product.customFields && product.customFields.length) {
    const variantCustomFieldIds = {};

    mergedVariant.customFields.forEach(({ fieldId }) => {
      variantCustomFieldIds[fieldId] = true;
    });

    product.customFields.forEach((row) => {
      const { fieldId } = row;
      if (!variantCustomFieldIds[fieldId]) {
        mergedVariant.customFields.push(row);
      }
    });
  }

  if (!mergedVariant.productTypeRateCharges) {
    mergedVariant.productTypeRateCharges = product.productTypeRateCharges;
  }

  if (!mergedVariant.productTypeDetails) {
    mergedVariant.productTypeDetails = product.productTypeDetails;
  }

  if (typeof mergedVariant.isSlab !== 'boolean') {
    mergedVariant.isSlab = product.isSlab;
  }

  if (mergedVariant.slabPrices === null) {
    mergedVariant.slabPrices = product.slabPrices;
  }

  if (typeof product.isSet === 'boolean' && product.isSet) {
    mergedVariant.isSlab = false;
  }

  return mergedVariant;
};

export const mergeProductVariantInventory = ({ parentProduct, inventory }) => {
  const amalgamInventory = getInventory();

  if (!parentProduct || !parentProduct.id) {
    return;
  }

  const productInventory = amalgamInventory[parentProduct.id];
  if (!productInventory) {
    return inventory;
  }

  const mergedInventory = { ...(inventory || {}) };
  if (!inventory.minOrderQuantity) {
    mergedInventory.minOrderQuantity = productInventory.minOrderQuantity;
  }
  return mergedInventory;
};

export const mergeNewVariantWithAmalgam = ({ product, inventory }) => {
  if (!product || !product.id) {
    return;
  }

  const amalgamObject = getAmalgamObject();
  const parentProduct = amalgamObject[product.parentProductId];
  const mergedProduct = mergeProductAndVariant(parentProduct, product);
  const mergedInventory = mergeProductVariantInventory({ parentProduct, inventory });

  if (!mergedProduct || !mergedProduct.id) {
    return;
  }

  deleteProductCache(mergedProduct.id);
  amalgamObject[mergedProduct.id] = mergedProduct;
  amalgamObject.inventory[mergedProduct.id] = mergedInventory;
};

export const saveSearchResultsIntoAmalgam = (data) => {
  const previousSavedResults = getAmalgamSavedSearchResults();

  // Concatenate productIds and deduplicate
  const concatenatedProductIds = (previousSavedResults.productIds || []).concat(
    data.productIds || []
  );
  const productIdsSet = new Set(concatenatedProductIds);
  const productIds = Array.from(productIdsSet);

  const newFilteredProducts = getFilteredProductIds(data);
  // Concatenate filteredProductIds and deduplicate
  const concatenatedFilteredProductIds = (previousSavedResults.filteredProductIds || []).concat(
    newFilteredProducts
  );
  const filteredProductIdsSet = new Set(concatenatedFilteredProductIds);
  const filteredProductIds = Array.from(filteredProductIdsSet);

  const updatedProducts = { ...previousSavedResults, ...data.products };
  const updatedInventory = { ...(previousSavedResults.inventory || {}), ...data.inventory };

  amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY] = {
    ...(amalgamObjects[COMMON_AMALGAM_KEY] || {}),
    ...updatedProducts,
    inventory: updatedInventory,
    productIds,
    filteredProductIds,
    searchTerm: data.searchTerm,
    page: data.page,
    totalProducts: data.totalProducts
  };

  getObjectValues(updatedProducts).forEach((variant) => {
    if (variant && variant.parentProductId) {
      mergeNewVariantWithAmalgam({
        product: variant,
        inventory: getSearchResultsInventoryByProductId(variant.id)
      });
    }
  });

  EventBus.notify(EventBus.TYPE.SEARCH_RESULTS_SAVED);
};

// Remove productIds where product is outOfStock (inventory count is 0) and showOutOfStockProduct is not true for them
const getFilteredProductIds = (data) => {
  const { productIds = [], inventory } = data;
  const updatedProductList = [];

  productIds.forEach((id) => {
    if (inventory[id].count !== 0 || getShowOutOfProductConfig(id)) {
      updatedProductList.push(id);
    }
  });
  return updatedProductList;
};

export const updateAmalgamForSearchClickedProduct = (data) => {
  const previousData = getAmalgamSavedSearchResults();
  const updatedProducts = { ...previousData, ...data };
  amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY] = {
    ...(amalgamObjects[COMMON_AMALGAM_KEY] || {}),
    ...updatedProducts
  };

  getObjectValues(updatedProducts).forEach((product) => {
    if (product) {
      deleteProductCache(product.id);

      if (product.parentProductId) {
        mergeNewVariantWithAmalgam({
          product,
          inventory: getSearchResultsInventoryByProductId(product.id)
        });
      }
    }
  });
};

export const getSearchResultsPaginationData = () => {
  const data = getAmalgamSavedSearchResults();
  return {
    searchTerm: data.searchTerm,
    page: data.page,
    totalProducts: data.totalProducts,
    savedProductsCount: (data.productIds || []).length,
    clickedProductId: data.clickedProductId
  };
};

export const clearSearchResults = () => {
  amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY] = {};
};

export const updatePicturesDimension = ({ productId, pictureId, width, height }) => {
  const amalgamObject = getAmalgamObject();
  if (amalgamObject[productId] && amalgamObject[productId].pictures[pictureId]) {
    amalgamObject[productId].pictures[pictureId].width = width;
    amalgamObject[productId].pictures[pictureId].height = height;
  }
};

export const setClickedProductId = (id) => {
  if (amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY]) {
    amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY].clickedProductId = id;
  }
};

export const getSearchResultsProductIds = () =>
  getAmalgamSavedSearchResults().filteredProductIds || [];

export const getSearchResultsInventoryByProductId = (id) =>
  (getAmalgamSavedSearchResults().inventory || {})[id] || {};

export const getAmalgamSavedSearchResults = () => amalgamObjects[SEARCH_RESULTS_AMALGAM_KEY] || {};

export const mergeProductsToAmalgam = (products) => {
  const amalgamObject = getAmalgamObject();

  amalgamObjects[getAmalgamKey()] = {
    ...amalgamObject,
    ...(products || {})
  };
};

export const mergeInventoryToAmalgam = (inventory) => {
  const amalgamObject = getAmalgamObject();

  amalgamObjects[getAmalgamKey()] = {
    ...amalgamObject,
    inventory: {
      ...(amalgamObject.inventory || {}),
      ...inventory
    }
  };
};

export const shouldShowWatchVideoButton = ({ productId }) => {
  const product = getProduct(productId);
  if (!product) {
    return false;
  }
  const { videos = {} } = product;

  return (
    Object.keys(videos).length > 0 &&
    Object.values(videos).filter((video) => video.prepared && video.urls).length > 0
  );
};

export const hideAddToListButtonBasedOnCoverIdAndPrice = (product) =>
  !(product || {}).formattedPrice && getCoverProductId() === product.id;

export const showCheckOfferSection = () => isPantaloonsCompanyId();

export const showOfferSection = () => false;
// return isPantaloonsCompanyId();

export const showMRP = () => isPantaloonsCompanyId();

export const showSizeGuide = (gender) =>
  isPantaloonsCompanyId() && PANTALOONS_IGNORE_GENDER_FOR_SIZE_GUIDE.indexOf(gender.trim()) === -1;

export const showCompanyBanner = () => isPantaloonsCompanyId();

export const directlyOpenWhatsAppOnChatClick = () => isPantaloonsCompanyId() || isCustomPage();

export const hideSendOrderReceiptOnEmail = () =>
  getCompanyExperiments().hideSendOrderReceiptOnEmail || isPantaloonsCompanyId();

const isPantaloonsCompanyId = () => getCompanyExperiments().showOfferButtonOnCatalogueProduct;

export const getShowReportAbuseLink = () =>
  (amalgamObjects[COMMON_AMALGAM_KEY] || {}).showReportAbuseLink;

export const shouldRemoveProductCountFromCatalogueTile = () =>
  getCompanyExperiments().removeProductCountFromCatalogueTile;

export const shouldRemoveProductCountFromCatalogueView = () =>
  getCompanyExperiments().removeProductCountFromCatalogueView;

export const getSlabPricesEnableStatus = (id) => {
  const product = getProduct(id);
  return product && product.isSlab && !!product.slabPrices;
};

export const setCustomPageScrollPosition = (scrollPosition) => {
  setCachedScrollPositionForPage({ scrollTop: scrollPosition });
};
export const getCustomPageScrollPosition = () => (getCachedScrollPositionForPage() || {}).scrollTop;

export const isSharedOrderOpened = () => (getAmalgamObject() || {}).isNewCartOpened || false;

export const getJewelleryPriceBreakUpConfig = () => {
  const defaultJewelleryPriceBreakUpConfig = {
    hideMetalGrossWeightPerGram: false,
    hideMetalNetWeightPerGram: false,
    hideMetalRatePerGram: false,
    hideMakingChargesSection: false,
    hideMakingChargesWeight: false,
    hideMakingChargesRate: false,
    hideDiamondStoneWeight: false,
    hideDiamondStoneRate: false,
    hideWastageChargesSection: false,
    hidePriceSummarySection: false,
    hideMetalSummarySection: false,
    hideStoneSummarySection: false,
    hidePriceSummarySubTotal: false,
    hidePriceSummaryDiscount: false,
    hidePriceSummaryTaxes: false,
    hidePriceSummaryTotal: false
  };
  try {
    const companyJewelleryPriceBreakUpConfig =
      getCompanyExperiments().jewelleryPriceBreakUpConfig || {};
    const catalogueJewelleryPriceBreakUpConfig = getExperiments().jewelleryPriceBreakUpConfig || {};
    return {
      ...defaultJewelleryPriceBreakUpConfig,
      ...companyJewelleryPriceBreakUpConfig,
      ...catalogueJewelleryPriceBreakUpConfig
    };
  } catch (error) {
    return { ...defaultJewelleryPriceBreakUpConfig };
  }
};

export const getCustomComponents = () => amalgamObjects[COMMON_AMALGAM_KEY].customComponents || [];

export const getProductGridTileComponent = () =>
  getCustomComponents().find(({ type }) => type === CUSTOM_COMPONENT_TYPES.PRODUCT_GRID_TILE) || {};

export const getProductListTileComponent = () =>
  getCustomComponents().find(({ type }) => type === CUSTOM_COMPONENT_TYPES.PRODUCT_LIST_TILE) || {};

export const getProductMagazineTileComponent = () =>
  getCustomComponents().find(({ type }) => type === CUSTOM_COMPONENT_TYPES.PRODUCT_MAGAZINE_TILE) ||
  {};

export const getCatalogueGridTileComponent = () =>
  getCustomComponents().find(({ type }) => type === CUSTOM_COMPONENT_TYPES.CATALOGUE_GRID_TILE) ||
  {};

export const getCatalogueFooterTheme = () =>
  amalgamObjects[COMMON_AMALGAM_KEY].customFooterTheme || {};

export const getCatalogueFooterSections = () => {
  const commonAmalgamObject = amalgamObjects[COMMON_AMALGAM_KEY];
  return (commonAmalgamObject.customFooter || [])
    .filter(({ isHidden }) => !isHidden)
    .sort((a, b) => a.order - b.order)
    .map(({ data = [], title, type } = {}) => ({
      data: data ? data.filter(({ value } = {}) => !!value) : [],
      title,
      type
    }))
    .filter(({ data } = {}) => !!data && data.length > 0);
};

export const initializeCustomFonts = () => {
  if (customFontsInitialized) {
    return;
  }
  try {
    const commonAmalgamObject = amalgamObjects[COMMON_AMALGAM_KEY];
    const customFonts = (commonAmalgamObject || {}).customFonts || [];
    const defaultFont = customFonts.find((font) => font.type === FONT_TYPES.DEFAULT);
    const headingFont = customFonts.find((font) => font.type === FONT_TYPES.HEADING);
    const subHeadingFont = customFonts.find((font) => font.type === FONT_TYPES.SUB_HEADING);
    const paragraphFont = customFonts.find((font) => font.type === FONT_TYPES.PARAGRAPH);
    const fallbackFontFamily = defaultFont
      ? defaultFont.isDefault
        ? DEFAULT_FONT_FAMILY
        : defaultFont.fontFamily
      : DEFAULT_FONT_FAMILY;
    customFonts.forEach((font) => {
      const style = document.createElement('style');
      style.innerHTML = `
        @font-face {
          font-family: "${font.fontFamily}";
          font-style: normal;
          font-weight: normal;
          font-display: swap;
          src: url("${font.url.replace('http://', 'https://')}") format("woff2");
        }
      `;
      document.head.appendChild(style);
    });
    if (defaultFont) {
      document
        .querySelector(':root')
        .style.setProperty(
          '--default-font',
          defaultFont.isDefault
            ? DEFAULT_FONT_FAMILY
            : `"${defaultFont.fontFamily}", ${DEFAULT_FONT_FAMILY}`
        );
    } else {
      document.querySelector(':root').style.setProperty('--default-font', DEFAULT_FONT_FAMILY);
    }
    if (headingFont) {
      document
        .querySelector(':root')
        .style.setProperty(
          '--heading-font',
          headingFont.isDefault
            ? DEFAULT_FONT_FAMILY
            : `"${headingFont.fontFamily}", ${DEFAULT_FONT_FAMILY}`
        );
    } else {
      document.querySelector(':root').style.setProperty('--heading-font', fallbackFontFamily);
    }
    if (subHeadingFont) {
      document
        .querySelector(':root')
        .style.setProperty(
          '--sub-heading-font',
          subHeadingFont.isDefault
            ? DEFAULT_FONT_FAMILY
            : `"${subHeadingFont.fontFamily}", ${DEFAULT_FONT_FAMILY}`
        );
    } else {
      document.querySelector(':root').style.setProperty('--sub-heading-font', fallbackFontFamily);
    }
    if (paragraphFont) {
      document
        .querySelector(':root')
        .style.setProperty(
          '--paragraph-font',
          paragraphFont.isDefault
            ? DEFAULT_FONT_FAMILY
            : `"${paragraphFont.fontFamily}", ${DEFAULT_FONT_FAMILY}`
        );
    } else {
      document.querySelector(':root').style.setProperty('--paragraph-font', fallbackFontFamily);
    }
  } catch (error) {
    logException('Custom fonts could not be initialized', {
      customFonts: (getAmalgamObject() || {}).customFonts
    });
  }
  customFontsInitialized = true;
};

export const getCompanyUnitDetails = () => amalgamObjects[COMMON_AMALGAM_KEY].unitDetails || [];

export const getCompanyUnitDetail = (setType) => {
  const companyUnitDetails = getCompanyUnitDetails();
  return companyUnitDetails.find((unitDetail) => unitDetail.setType === setType) || {};
};
